//---------------------------------------------------------------------------------------------------
// ## IMPORTS ##
// Library Imports
import { useRef, useState, useEffect } from "react";
import { v4 as uuid } from "uuid";
import * as THREE from "three";

// Material ui Imports
import {
    Box,
    CircularProgress,
    Grid,
    ImageList,
    ImageListItem,
    LinearProgress,
    Skeleton,
    Switch,
    SxProps,
    Tooltip,
    Typography,
    useTheme,
} from "@mui/material";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";

// Custom components imports (and hooks and helpers)
import {
    VTButton,
    VTCanvas,
    VTSidePanel360MediaCard,
    VTTextField,
    VTTypography,
    VTVideoControls,
} from "@virtus-tech-repository/virtus-tech-repository";
import {
    EDraggableOptions,
    EMediaOptions,
    IMediaItem,
} from "@virtus-tech-repository/virtus-tech-repository/lib/models/media.model";
import { Preview } from "@mui/icons-material";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import {
    useCreateAllImagesMutation,
    useDeleteMediaMutation,
    useGetAll360ImagesQuery,
    useGetAll360VideosQuery,
    usePreSignedUrlMutation,
    useUpdateAllScenarioMediaMutation,
} from "../../services/media.service";
import { uploadMedia } from "../../store/thunks/upload.thunks";
import { setMediaUploadCleanUp } from "../../store/slices/mediaUpload.slice";
import VTImmersiveVideoDraggable from "./VTImmersiveVideoDraggable";
import VTCreatorVideoControls from "./VTCreatorVideoControls";
import { Canvas } from "@react-three/fiber";
// Media imports
import NoVideos from "../../assets/images/NoVideos.svg";
import { Params, useParams } from "react-router-dom";
import SearchNotFound from "../../assets/images/NoVideos.svg";
import VTSphere from "./VTSphere";
import { ExposedCameraControlFunctions } from "@virtus-tech-repository/virtus-tech-repository/lib/components/VTCanvasComponents/VTCameraControls";
import { setCurrentDoubleClick, setCurrentToast } from "../../store/slices/current.slice";
import { Draggable, Droppable } from "react-beautiful-dnd";
import { IDragOptions } from "@virtus-tech-repository/virtus-tech-repository/lib/models/current.model";
import { EDroppableContainerId } from "@virtus-tech-repository/virtus-tech-repository/lib/models/DragAndDrop.models";
import VTImmersiveVideoUpload from "./VTImmersiveVideoUpload";
import WordingHelper from "../../helpers/wordingHelper";
import VTMediaUploader from "./VTMediaUploader";
//---------------------------------------------------------------------------------------------------
// ## MUI STYLED ##
// Layout stuff remains inline, other styling pulled into it's own components
//---------------------------------------------------------------------------------------------------
// ## INTERFACES ##
interface IProps {
    sx?: SxProps;
    handleDragStart: any;
    handleDragStop: () => void;
}

//---------------------------------------------------------------------------------------------------
// ## COMPONENTS ##
// Only display layouts in pages
export default function VTImmersiveVideoSidePanel({ handleDragStart, handleDragStop, sx }: any) {
    //-------------------------------------------------------------------------------------------------
    // ## HOOKS ##
    const inputFileRef = useRef<HTMLInputElement>(null);
    const cameraControlsRef = useRef<ExposedCameraControlFunctions>(null);
    const { id: userId, organisation } = useAppSelector((state) => state.userReducer);

    const [preSignedUrl, { data: uploadUrl, isLoading: preSignedUrlLoading }] = usePreSignedUrlMutation();

    const { mediaUploadPercentage, mediaUploadCompleted, mediaUploadError, mediaUploading } = useAppSelector(
        (state) => state.mediaUploadReducer,
    );

    const [video, setVideo] = useState<HTMLVideoElement>(document.createElement("video"));
    const [videoCurrentTime, setVideoCurrentTime] = useState<number>(0);
    const [videoCurrentState, setVideoCurrentState] = useState<"Paused" | "Playing" | "Question" | undefined>(
        undefined,
    );
    const { scenarioId, sceneId }: Readonly<Params<string>> = useParams();

    const [createImmersiveVideos] = useCreateAllImagesMutation();

    const [selectedVideo, setSelectedVideo] = useState<File | null>(null);
    const [selectedVideoError, setSelectedVideoError] = useState<boolean>(false);
    const [videoName, setVideoName] = useState<string>("");
    const [videoId, setVideoId] = useState<string>(uuid());
    const [videoPreview, setVideoPreview] = useState<string | null>(null);
    const [capturePreview, setCapturePreview] = useState<boolean>(false);

    const [highlight, setHighlight] = useState(false);

    const { data: videos, isLoading: videosLoading } = useGetAll360VideosQuery(userId);
    const { data: scenarioVideos, isLoading: scenarioVideosLoading } = useGetAll360VideosQuery(scenarioId!);

    const [updateAllScenarioMedia, { isLoading: updateLoading }] = useUpdateAllScenarioMediaMutation();
    const [deleteAllScenarioMedia, { isLoading: deleteLoading }] = useDeleteMediaMutation();

    const [currentItemUpdate, setCurrentItemUpdate] = useState<string | undefined>(undefined);

    const dispatch = useAppDispatch();

    const { currentScenarioIsUpdating, currentlyDragging } = useAppSelector((state) => state.currentReducer);

    const theme = useTheme();
    //-------------------------------------------------------------------------------------------------
    // ## USE SELECTOR ##

    //-------------------------------------------------------------------------------------------------
    // ## USE STATE ##
    const [activeButton, setActiveButton] = useState(0);
    const [searchValue, setSearchValue] = useState<string>("");
    const [checked, setChecked] = useState(false);

    //-------------------------------------------------------------------------------------------------
    // ## USE EFFECTS ##
    useEffect(() => {
        if (mediaUploadCompleted && selectedVideo) {
            if (checked) {
                createImmersiveVideos({
                    id: userId,
                    media_id: videoId,
                    media_type: "three_sixty_video",
                    filetype: selectedVideo.type.split("/")[1],
                    name: videoName,
                    description: "",
                    date: Date.now(),
                    tags: [],
                    preview: capturePreview,
                    organisation: organisation,
                    chopping: "processing",
                });
            } else {
                createImmersiveVideos({
                    id: userId,
                    media_id: videoId,
                    media_type: "three_sixty_video",
                    filetype: selectedVideo.type.split("/")[1],
                    name: videoName,
                    description: "",
                    date: Date.now(),
                    tags: [],
                    preview: capturePreview,
                    organisation: organisation,
                });
            }
        }
    }, [mediaUploadCompleted]);

    //-------------------------------------------------------------------------------------------------
    // ## CUSTOM FUNCTIONS ##

    const handleButtonClick = (buttonIndex: number) => {
        setActiveButton(buttonIndex);
    };

    //-------------------------------------------------------------------------------------------------
    // Function to get src and size images based on size
    function srcset(image: string, size: number, rows = 1, cols = 1) {
        return {
            src: `${image}?w=${size * cols}&h=${size * rows}&fit=crop&auto=format`,
            srcSet: `${image}?w=${size * cols}&h=${size * rows}&fit=crop&auto=format&dpr=2 2x`,
        };
    }

    //-------------------------------------------------------------------------------------------------
    // Function to assign image widths
    function colSizeSwitch(param: number) {
        switch (param) {
            case 0:
                return 2;
            case 1:
                return 1;
            case 2:
                return 1;
            case 3:
                return 2;
            case 4:
                return 2;
            case 5:
                return 2;
            case 6:
                return 1;
            case 7:
                return 1;
            case 8:
                return 2;
        }
    }

    //-------------------------------------------------------------------------------------------------
    // Function to assign image heights
    function rowSizeSwitch(param: number) {
        switch (param) {
            case 0:
                return 2;
            case 1:
                return 1;
            case 2:
                return 1;
            case 3:
                return 1;
            case 4:
                return 1;
            case 5:
                return 2;
            case 6:
                return 1;
            case 7:
                return 1;
            case 8:
                return 1;
        }
    }

    function localHandleDrag(
        dragged: boolean,
        mediaType?: EDraggableOptions,
        src?: string,
        preview?: string | undefined,
    ) {
        if (dragged && handleDragStart && mediaType && src) {
            handleDragStart(mediaType, src, preview);
        } else if (!dragged && handleDragStop) {
            handleDragStop();
        }
    }

    function preventDefaults(e: React.DragEvent) {
        e.preventDefault();
        e.stopPropagation();
    }

    async function handleFileAdded(videoFile: File) {
        // TODO: in the future only allow certain videos types
        if (videoFile.type.includes("video/")) {
            setSelectedVideoError(false);
            setSelectedVideo(videoFile);
            setVideoName(videoFile.name.split(".")[0]);

            if (checked) {
                // Get presigned url to upload video
                await preSignedUrl({
                    organisation,
                    media_type: "three_sixty_video",
                    media_id: `${videoId}/raw`,
                    filetype: videoFile.type.split("/")[1],
                });
            } else {
                // Get presigned url to upload video
                await preSignedUrl({
                    organisation,
                    media_type: "three_sixty_video",
                    media_id: `${videoId}/${videoId}`,
                    filetype: videoFile.type.split("/")[1],
                });
            }
        } else {
            setSelectedVideoError(true);
        }
    }

    async function handleGetPreviewURL() {
        // Get presigned url to upload image
        await preSignedUrl({
            organisation,
            media_type: "three_sixty_video",
            media_id: `${videoId}/${videoId}_preview`,
            filetype: "png",
        });
    }

    // Function that handle image added from dropping and dragging in
    function handleDrop(e: React.DragEvent) {
        preventDefaults(e);
        setHighlight(false);

        if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
            handleFileAdded(e.dataTransfer.files[0]);
        }
    }

    // Function that handle video added from file system
    function handleSelect(e: React.ChangeEvent<HTMLInputElement>) {
        setHighlight(false);

        if (e.target.files && e.target.files.length > 0) {
            handleFileAdded(e.target.files[0]);
        }
    }

    function handleVideoUpload() {
        if (uploadUrl && selectedVideo) {
            dispatch(
                uploadMedia({
                    media: selectedVideo,
                    url: uploadUrl.url,
                    dispatch: dispatch,
                }),
            );
        }
    }

    async function handlePreviewUpload() {
        if (videoPreview) {
            const file = await dataUrlToFile(videoPreview, "preview");

            if (uploadUrl && videoPreview) {
                dispatch(
                    uploadMedia({
                        media: file,
                        url: uploadUrl.url,
                        dispatch: dispatch,
                    }),
                );
            }
        }
    }

    function handleUploadCleanUp() {
        setSelectedVideo(null);
        setVideoId(uuid());
        setVideoName("");
        dispatch(setMediaUploadCleanUp());
        setActiveButton(0);
        setCapturePreview(false);
    }

    async function dataUrlToFile(dataUrl: string, fileName: string): Promise<File> {
        const res: Response = await fetch(dataUrl);
        const blob: Blob = await res.blob();
        return new File([blob], fileName, { type: "image/png" });
    }

    function handleSwitchChange(event: React.ChangeEvent<HTMLInputElement>) {
        setChecked(event.target.checked);

        if (event.target.checked) {
            dispatch(
                setCurrentToast({
                    id: "360_video_process_toast",
                    message: "This will proccess the 360 video metadata and upload for you.",
                }),
            );
        }
    }
    //-------------------------------------------------------------------------------------------------
    // ## RETURN ##
    // Show available Videos
    return (
        <Grid container xs={12} justifyContent={"center"} sx={{ overflowY: "hidden", userSelect: "none", ...sx }}>
            {/*-----------------------------------------------------------------------------------------*/}
            {/* Video search */}
            <Grid item xs={11}>
                <VTTextField
                    placeholder={"Search 360 Videos"}
                    sx={{
                        marginTop: "20px",
                    }}
                    value={searchValue}
                    onChange={(textInput: string) => setSearchValue(textInput)}
                    type={"text"}
                    endIcon={<SearchRoundedIcon />}
                />
            </Grid>
            {/*-----------------------------------------------------------------------------------------*/}
            {/* Filters */}
            <Grid
                item
                container
                xs={11}
                sx={{
                    marginTop: "15px",
                    display: "flex",
                    overflowX: "auto",
                }}
            >
                <Grid item xs={4}>
                    <VTButton
                        onClick={() => handleButtonClick(0)}
                        sx={{
                            borderBottom: activeButton === 0 ? `3px solid ${theme.palette.primary.main}` : "none",
                            maxWidth: "100%",
                        }}
                        type="underline"
                    >
                        <Typography>All Videos</Typography>
                    </VTButton>
                </Grid>

                <Grid item xs={4}>
                    {scenarioVideos && (
                        <VTButton
                            onClick={() => handleButtonClick(2)}
                            sx={{
                                borderBottom: activeButton === 2 ? `3px solid ${theme.palette.primary.main}` : "none",
                                maxWidth: "100%",
                            }}
                            type="underline"
                        >
                            <Typography>{WordingHelper(organisation)} Videos</Typography>
                        </VTButton>
                    )}
                </Grid>
                <Grid item xs={4}>
                    <VTButton
                        onClick={() => handleButtonClick(1)}
                        sx={{
                            borderBottom: activeButton === 1 ? `3px solid ${theme.palette.primary.main}` : "none",
                            maxWidth: "100%",
                        }}
                        type="underline"
                    >
                        <Typography>Upload Videos</Typography>
                    </VTButton>
                </Grid>
            </Grid>
            {/*-----------------------------------------------------------------------------------------*/}
            {/* Video upload */}
            {activeButton === 1 && (
                <VTMediaUploader
                    onComplete={() => setActiveButton(0)}
                    sx={{ overflowX: "auto", height: "calc(100vh - 224px)" }}
                    context={"Immersive Videos"}
                />
            )}
            {activeButton === 0 && (
                <>
                    {/*-----------------------------------------------------------------------------------------*/}
                    {/* Video list */}
                    {videosLoading ? (
                        <Box
                            component="div"
                            sx={{
                                marginTop: "5px",
                                overflowY: "scroll",
                                width: "100%",
                                padding: "20px",
                                height: "calc(100vh - 230px)",
                            }}
                        >
                            <VTSidePanel360MediaCard sx={{ marginBottom: "8px" }} name={""} loading={true} />
                            <VTSidePanel360MediaCard sx={{ marginBottom: "8px" }} name={""} loading={true} />
                            <VTSidePanel360MediaCard sx={{}} name={""} loading={true} />
                        </Box>
                    ) : (
                        <>
                            {!videos || videos.length === 0 ? (
                                <Grid item xs={12} textAlign={"center"} justifyContent={"center"}>
                                    <img src={NoVideos} style={{ maxWidth: "90%", marginTop: "20px" }} />
                                    <VTTypography sx={{ color: "grey", padding: "10px" }}>
                                        To get started, add first video file
                                    </VTTypography>
                                </Grid>
                            ) : (
                                <Box
                                    // ref={provided.innerRef}
                                    // {...provided.droppableProps}
                                    component="div"
                                    sx={{
                                        marginTop: "5px",
                                        overflowY: "scroll",
                                        width: "100%",
                                        padding: "20px",
                                        height: "calc(100vh - 230px)",
                                    }}
                                >
                                    {videos?.filter(
                                        (item: IMediaItem) =>
                                            searchValue === "" ||
                                            item.name!.toLowerCase().includes(searchValue.toLocaleLowerCase()) ||
                                            item.description!.toLowerCase().includes(searchValue.toLocaleLowerCase()),
                                    ).length === 0 ? (
                                        <Grid item xs={12} textAlign={"center"}>
                                            <img src={SearchNotFound} style={{ width: "90%", marginTop: "20px" }} />
                                            <Typography sx={{ color: "grey", padding: "10px" }}>
                                                No matches found
                                            </Typography>
                                        </Grid>
                                    ) : (
                                        <Droppable
                                            droppableId={EDroppableContainerId.MEDIA_360_VIDEOS}
                                            type="DROPPABLE_MEDIA"
                                            // isDropDisabled={false}
                                            // renderClone={(provided, snapshot, rubric) => (
                                            //     <div
                                            //         {...provided.draggableProps}
                                            //         {...provided.dragHandleProps}
                                            //         ref={provided.innerRef}
                                            //     >
                                            //         Item id: {images[rubric.source.index].id}
                                            //     </div>
                                            // )}
                                        >
                                            {(provided, snapshot) => (
                                                <div
                                                    ref={provided.innerRef}
                                                    {...provided.droppableProps}
                                                    style={{ transform: "none !important" }}
                                                >
                                                    {videos
                                                        ?.filter(
                                                            (item: IMediaItem) =>
                                                                searchValue === "" ||
                                                                item
                                                                    .name!.toLowerCase()
                                                                    .includes(searchValue.toLocaleLowerCase()) ||
                                                                item
                                                                    .description!.toLowerCase()
                                                                    .includes(searchValue.toLocaleLowerCase()),
                                                        )
                                                        .sort((a: IMediaItem, b: IMediaItem) => {
                                                            return (
                                                                new Date(b.date).valueOf() - new Date(a.date).valueOf()
                                                            );
                                                        })
                                                        .map((item: IMediaItem, i) => {
                                                            console.log("item preview", item.preview);
                                                            return (
                                                                <Draggable
                                                                    key={item.id}
                                                                    draggableId={`${process.env.REACT_APP_NINJA_MEDIA}/${organisation}/three_sixty_video/${item.id}/${item.id}.${item.filetype}`}
                                                                    index={i}
                                                                    isDragDisabled={
                                                                        item.chopping === "error" ||
                                                                        item.chopping === "processing"
                                                                    }
                                                                >
                                                                    {(provided, snapshot) => (
                                                                        <>
                                                                            <VTSidePanel360MediaCard
                                                                                {...provided.draggableProps}
                                                                                {...provided.dragHandleProps}
                                                                                ref={provided.innerRef}
                                                                                sx={{
                                                                                    marginBottom: "8px",
                                                                                    // zIndex: 1,
                                                                                    transform:
                                                                                        !snapshot.isDragging ||
                                                                                        (currentlyDragging &&
                                                                                            !snapshot.isDragging)
                                                                                            ? "none !important"
                                                                                            : "auto",
                                                                                    ...(snapshot.isDragging && {
                                                                                        border: `4px dashed ${theme.palette.primary.light}`,
                                                                                        borderRadius: "16px",
                                                                                    }),
                                                                                }}
                                                                                cardType360="video"
                                                                                loading={currentScenarioIsUpdating}
                                                                                processingErrorMsg={
                                                                                    item.chopping === "error"
                                                                                        ? "There has been an error processing this image, please try again"
                                                                                        : undefined
                                                                                }
                                                                                processingMsg={
                                                                                    item.chopping === "processing"
                                                                                        ? "The image is being processed, please wait and refresh your page"
                                                                                        : undefined
                                                                                }
                                                                                pathTo360Images={undefined}
                                                                                previewImageSrc={`${process.env.REACT_APP_NINJA_MEDIA}/${organisation}/three_sixty_video/${item.id}/${item.id}_preview.jpg`}
                                                                                name={item.name!}
                                                                                onHandleDoubleClick={() => {
                                                                                    if (
                                                                                        item.chopping ===
                                                                                            "processing" ||
                                                                                        item.chopping === "error"
                                                                                    ) {
                                                                                        return;
                                                                                    }

                                                                                    dispatch(
                                                                                        setCurrentDoubleClick({
                                                                                            src: `${process.env.REACT_APP_NINJA_MEDIA}/${organisation}/three_sixty_video/${item.id}/${item.id}.${item.filetype}`,
                                                                                            mediaType:
                                                                                                EDraggableOptions.IMMERSIVE_VIDEO,
                                                                                        } as IDragOptions),
                                                                                    );
                                                                                }}
                                                                                onHandleDragStart={() => {
                                                                                    // console.log("on handle drag start");
                                                                                    // localHandleDrag(
                                                                                    //     true,
                                                                                    //     EDragContentOptions.IMAGE_360,
                                                                                    //     `${process.env.REACT_APP_NINJA_MEDIA}/${organisation}/three_sixty_video/${item.id}/${item.id}.${item.filetype}`,
                                                                                    // );
                                                                                }}
                                                                                onHandleDragEnd={() => {
                                                                                    // console.log("on handle drag end");
                                                                                    // localHandleDrag(false);
                                                                                }}
                                                                            />{" "}
                                                                            {snapshot.isDragging && (
                                                                                <div
                                                                                    style={{
                                                                                        backgroundColor:
                                                                                            theme.palette.background
                                                                                                .paper,
                                                                                        width: "100%",
                                                                                        height: "103px",
                                                                                        borderRadius: "15px",
                                                                                        transform: "none !important",
                                                                                        marginBottom: "10px",
                                                                                    }}
                                                                                ></div>
                                                                            )}
                                                                        </>
                                                                    )}
                                                                </Draggable>
                                                                // <VTImmersiveVideoDraggable
                                                                //     key={item.id}
                                                                //     item={item}
                                                                //     organisation={organisation}
                                                                //     localHandleDrag={localHandleDrag}
                                                                //     handleDoubleClick={handleDoubleClick}
                                                                // />
                                                            );
                                                        })}
                                                    {provided.placeholder}
                                                </div>
                                            )}
                                        </Droppable>
                                    )}
                                </Box>
                            )}
                        </>
                    )}
                </>
            )}

            {activeButton === 2 && (
                <>
                    {/*-----------------------------------------------------------------------------------------*/}
                    {/* Video list */}
                    {scenarioVideosLoading ? (
                        <Box
                            component="div"
                            sx={{
                                marginTop: "5px",
                                overflowY: "scroll",
                                width: "100%",
                                padding: "20px",
                                height: "calc(100vh - 230px)",
                            }}
                        >
                            <VTSidePanel360MediaCard sx={{ marginBottom: "8px" }} name={""} loading={true} />
                            <VTSidePanel360MediaCard sx={{ marginBottom: "8px" }} name={""} loading={true} />
                            <VTSidePanel360MediaCard sx={{}} name={""} loading={true} />
                        </Box>
                    ) : (
                        <>
                            {!scenarioVideos || scenarioVideos.length === 0 ? (
                                <Grid item xs={12} textAlign={"center"}>
                                    <img src={NoVideos} style={{ maxWidth: "90%", marginTop: "20px" }} />
                                    <VTTypography sx={{ color: "grey", padding: "10px" }}>
                                        To get started, add first video file
                                    </VTTypography>
                                </Grid>
                            ) : (
                                <Box
                                    component="div"
                                    sx={{
                                        marginTop: "5px",
                                        overflowY: "scroll",
                                        width: "100%",
                                        padding: "20px",
                                        height: "calc(100vh - 230px)",
                                    }}
                                >
                                    {scenarioVideos?.filter(
                                        (item: IMediaItem) =>
                                            searchValue === "" ||
                                            item.name!.toLowerCase().includes(searchValue.toLocaleLowerCase()) ||
                                            item.description!.toLowerCase().includes(searchValue.toLocaleLowerCase()),
                                    ).length === 0 ? (
                                        <Grid item xs={12} textAlign={"center"}>
                                            <img src={SearchNotFound} style={{ width: "90%", marginTop: "20px" }} />
                                            <Typography sx={{ color: "grey", padding: "10px" }}>
                                                No matches found
                                            </Typography>
                                        </Grid>
                                    ) : (
                                        scenarioVideos
                                            ?.filter(
                                                (item: IMediaItem) =>
                                                    searchValue === "" ||
                                                    item
                                                        .name!.toLowerCase()
                                                        .includes(searchValue.toLocaleLowerCase()) ||
                                                    item
                                                        .description!.toLowerCase()
                                                        .includes(searchValue.toLocaleLowerCase()),
                                            )
                                            .sort((a: IMediaItem, b: IMediaItem) => {
                                                return new Date(b.date).valueOf() - new Date(a.date).valueOf();
                                            })
                                            .map((item: IMediaItem, index: number) => (
                                                // <>
                                                //     {(updateLoading || deleteLoading) &&
                                                //     currentItemUpdate == item.id ? (
                                                //         <Grid item xs={6}>
                                                //             <Skeleton
                                                //                 sx={{
                                                //                     height: "300px",
                                                //                     margin: "10px",
                                                //                     marginBlock: "-25px",
                                                //                     marginTop: "-55px",
                                                //                 }}
                                                //             />
                                                //         </Grid>
                                                //     ) : (
                                                <VTSidePanel360MediaCard
                                                    sx={{
                                                        marginBottom: "8px",
                                                        zIndex: 1,
                                                    }}
                                                    cardType360="video"
                                                    loading={
                                                        (updateLoading || deleteLoading) && currentItemUpdate == item.id
                                                    }
                                                    processingErrorMsg={
                                                        item.chopping === "error"
                                                            ? "There has been an error processing this image, please try again"
                                                            : undefined
                                                    }
                                                    processingMsg={
                                                        item.chopping === "processing"
                                                            ? "The image is being processed, please wait and refresh your page"
                                                            : undefined
                                                    }
                                                    pathTo360Images={
                                                        item.chopping !== "processing" && item.chopping !== "error"
                                                            ? undefined
                                                            : undefined
                                                    }
                                                    previewImageSrc={`${process.env.REACT_APP_NINJA_MEDIA}/${organisation}/three_sixty_video/${item.id}/${item.id}_preview.jpg`}
                                                    name={item.name!}
                                                    onAddButtonClicked={() => {
                                                        setCurrentItemUpdate(item.id);
                                                        updateAllScenarioMedia({
                                                            id: userId,
                                                            media_type: EDraggableOptions.IMMERSIVE_VIDEO,
                                                            media_ids: [item.id],
                                                        });
                                                    }}
                                                    onHandleDoubleClick={() => {}}
                                                    onHandleDragStart={() => {
                                                        // console.log("on handle drag start");
                                                        // localHandleDrag(
                                                        //     true,
                                                        //     EDragContentOptions.IMAGE_360,
                                                        //     `${process.env.REACT_APP_NINJA_MEDIA}/${organisation}/three_sixty_video/${item.id}/${item.id}.${item.filetype}`,
                                                        // );
                                                    }}
                                                    onHandleDragEnd={() => {
                                                        // console.log("on handle drag end");
                                                        // localHandleDrag(false);
                                                    }}
                                                />
                                                // <VTImmersiveVideoDraggable
                                                //     item={item}
                                                //     organisation={organisation}
                                                //     localHandleDrag={localHandleDrag}
                                                //     handleDoubleClick={handleDoubleClick}
                                                //     addFunction={() => {
                                                //         setCurrentItemUpdate(item.id);
                                                //         updateAllScenarioMedia({
                                                //             id: userId,
                                                //             media_type: EMedia.VIDEO_360,
                                                //             media_ids: [item.id],
                                                //         });
                                                //     }}
                                                //     deleteFunction={() => {
                                                //         setCurrentItemUpdate(item.id);
                                                //         deleteAllScenarioMedia({
                                                //             mediaType: EMedia.VIDEO_360,
                                                //             collectionId: scenarioId!,
                                                //             mediaId: item.id,
                                                //         });
                                                //     }}
                                                // />
                                                //     )}
                                                // </>
                                            ))
                                    )}
                                </Box>
                            )}
                        </>
                    )}
                </>
            )}
        </Grid>
    );
}
